import { useState } from "react";
import { motion } from "framer-motion";
import { LogOut, Search, ShieldAlert, UserCheck } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";
import {
  forceLogout,
  impersonate,
  searchOrgs,
  searchUsers,
  type AdminOrgRow,
  type AdminUserRow,
} from "@/api/adminConsole";

/**
 * Platform support console — `is_platform_admin` only.
 *
 * Three operations live here, each with a deliberately small UI surface so
 * the operator has to pick the right hammer:
 *   1. Cross-tenant search (users + orgs).
 *   2. Impersonation — issues a 15-min access token that opens a new tab.
 *   3. Force-logout — revokes every refresh-token row for a user.
 *
 * Both impersonate and force-logout require a typed `reason` so the audit
 * row carries some explanation later. Buttons are disabled until the form
 * passes a minimal sanity check (target chosen + reason ≥ 3 chars).
 */
export function SupportConsolePage() {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [scope, setScope] = useState<"users" | "orgs">("users");
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [orgs, setOrgs] = useState<AdminOrgRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Action modal — shared between impersonate and force-logout.
  const [action, setAction] = useState<null | {
    kind: "impersonate" | "force-logout";
    target: AdminUserRow;
  }>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const closeModal = () => {
    setAction(null);
    setReason("");
    setSubmitting(false);
  };

  if (!user?.is_platform_admin) {
    // Defense-in-depth — the route is also gated, but a stale auth state
    // or a user whose flag was revoked mid-session shouldn't reach inputs.
    return (
      <div className="flex flex-col items-center justify-center py-24 text-text-muted">
        <ShieldAlert className="h-10 w-10" />
        <p className="mt-4">Platform admin privileges required.</p>
      </div>
    );
  }

  const onSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      if (scope === "users") {
        setUsers(await searchUsers(query.trim()));
      } else {
        setOrgs(await searchOrgs(query.trim()));
      }
    } catch {
      addToast("error", "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async () => {
    if (!action) return;
    if (reason.trim().length < 3) {
      addToast("error", "Reason must be at least 3 characters");
      return;
    }
    setSubmitting(true);
    try {
      if (action.kind === "impersonate") {
        const r = await impersonate(action.target.id, reason.trim());
        // Drop the impersonation token into a new tab via a one-shot
        // bootstrap URL. The receiving page reads it from a hash fragment
        // so it never lands in browser history or referrer headers.
        const url = `/?impersonation=${encodeURIComponent(r.access_token)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        addToast("success", `Impersonation token issued (expires in ${r.expires_in / 60} min)`);
      } else {
        await forceLogout(action.target.id, reason.trim());
        addToast("success", `${action.target.email} signed out everywhere`);
      }
      closeModal();
    } catch {
      addToast("error", "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Support Console"
        subtitle="Cross-tenant search · impersonate · force-logout"
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search
          </CardTitle>
        </CardHeader>

        <div className="space-y-4 p-4">
          <div className="flex gap-2">
            <select
              className="rounded-md border bg-background px-2 py-1 text-sm"
              value={scope}
              onChange={(e) => setScope(e.target.value as "users" | "orgs")}
            >
              <option value="users">Users</option>
              <option value="orgs">Orgs</option>
            </select>
            <Input
              value={query}
              placeholder={
                scope === "users" ? "email or UUID" : "name, slug, or UUID"
              }
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void onSearch();
              }}
            />
            <Button onClick={() => void onSearch()} disabled={loading}>
              Search
            </Button>
          </div>

          {/* Users table */}
          {scope === "users" && (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Org</th>
                    <th className="px-3 py-2">Role</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && !loading && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                        No results.
                      </td>
                    </tr>
                  )}
                  {users.map((u) => (
                    <tr key={u.id} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                      <td className="px-3 py-2">
                        <div className="text-xs">{u.org.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {u.org.slug} · {u.org.plan}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge>{u.role}</Badge>
                        {u.is_platform_admin && (
                          <Badge className="ml-1" variant="warning">
                            platform
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {u.is_active ? "active" : "disabled"}
                        {u.mfa_enabled ? " · MFA" : ""}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={u.is_platform_admin}
                          onClick={() => setAction({ kind: "impersonate", target: u })}
                        >
                          <UserCheck className="mr-1 h-3 w-3" /> Impersonate
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setAction({ kind: "force-logout", target: u })}
                        >
                          <LogOut className="mr-1 h-3 w-3" /> Force-logout
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Orgs table */}
          {scope === "orgs" && (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Slug</th>
                    <th className="px-3 py-2">Plan</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.length === 0 && !loading && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                        No results.
                      </td>
                    </tr>
                  )}
                  {orgs.map((o) => (
                    <tr key={o.id} className="border-t">
                      <td className="px-3 py-2">{o.name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{o.slug}</td>
                      <td className="px-3 py-2">
                        <Badge>{o.plan}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {o.subscription_status ?? "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      <Modal
        isOpen={action !== null}
        onClose={closeModal}
        title={
          action?.kind === "impersonate"
            ? `Impersonate ${action.target.email}`
            : `Force-logout ${action?.target.email ?? ""}`
        }
      >
        {action && (
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              {action.kind === "impersonate"
                ? "Opens a new tab signed in as this user for 15 minutes. The audit log records both you and the target."
                : "Revokes every active refresh token for this user. They will be forced to sign in again on the next request."}
            </p>
            <Input
              label="Reason (audit log)"
              placeholder="e.g. Reproduce ticket #1234"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={() => void onConfirm()}
                disabled={submitting || reason.trim().length < 3}
              >
                {submitting
                  ? "Working…"
                  : action.kind === "impersonate"
                  ? "Issue token"
                  : "Force logout"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
}
